import React, { useEffect, useState } from "react";
import Loader from "../../../components/Loader/Loader";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import Alert from "../../../components/v3/Alert";
import Empty from "../../../components/v3/Empty";
import Field from "../../../components/v3/Field";
import Btn from "../../../components/v3/Btn";
import Pill from "../../../components/v3/Pill";
import { formatAuditTimestamp } from "../../../utils/fmtDate";
import { bdcApi } from "../../../services/bdcApi";

const CATEGORIES = [
  "", "KYC", "SUITABILITY", "CLASSIFICATION", "INVESTOR_APPROVAL",
  "DEAL_APPROVAL", "REFERRAL", "COMMISSION", "PAYOUT", "ATTRIBUTION",
  "CREDENTIAL", "ROLE", "CAMPAIGN", "SYSTEM", "PAYMENT", "COMPLIANCE",
];

function AuditLogViewer() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState("");
  const [actorUserId, setActorUserId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  function fetchList() {
    setLoading(true);
    const params = { page, limit: 50 };
    if (category) params.category = category;
    if (actorUserId) params.actorUserId = actorUserId;
    if (from) params.from = from;
    if (to) params.to = to;
    bdcApi.adListAuditLog(params)
      .then((res) => {
        const d = res?.data || {};
        setRows(Array.isArray(d.rows) ? d.rows : []);
        setTotal(d.total || 0);
        setTotalPages(d.total_pages || 0);
        setError(null);
      })
      .catch((err) => {
        setError(err?.response?.data?.message || err.message || "Fetch failed");
      })
      .finally(() => setLoading(false));
  }

  function resetFilters() {
    setCategory("");
    setActorUserId("");
    setFrom("");
    setTo("");
    setPage(1);
  }

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  return (
    <PageShell>
      <Loader isLoading={loading} />
      <PageHead
        title="Audit log"
        subtitle={<><span className="mono">{total}</span><span>event{total === 1 ? "" : "s"}</span></>}
      />

      <p style={{ color: "var(--tx-3)", fontSize: "var(--t-sm)", margin: "0 0 12px" }}>
        Every mutating admin action across the platform. Paired with the compliance CSV export for regulator-facing submissions.
      </p>

      <section className="card">
        <div className="card__body">
          <form
            onSubmit={(e) => { e.preventDefault(); setPage(1); fetchList(); }}
            style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}
          >
            <div style={{ minWidth: 200 }}>
              <Field
                as="select"
                label="Category"
                name="category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                {CATEGORIES.map((c) => (
                  <option key={c || "all"} value={c}>{c || "All categories"}</option>
                ))}
              </Field>
            </div>
            <div style={{ minWidth: 140 }}>
              <Field
                label="Actor user id"
                name="actor_user_id"
                type="number"
                value={actorUserId}
                onChange={(e) => setActorUserId(e.target.value)}
                placeholder="123"
              />
            </div>
            <div style={{ minWidth: 160 }}>
              <Field label="From" name="from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div style={{ minWidth: 160 }}>
              <Field label="To" name="to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
            <Btn variant="primary" type="submit">Apply</Btn>
            <Btn variant="ghost" type="button" onClick={resetFilters}>Reset</Btn>
          </form>
        </div>
      </section>

      {error && <Alert tone="danger">{error}</Alert>}

      {rows.length === 0 ? (
        <Empty>No audit events match these filters.</Empty>
      ) : (
        <section className="card">
          <div className="card__body card__body--flush">
            <table className="tbl">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Actor</th>
                  <th>Role</th>
                  <th>Category</th>
                  <th>Action</th>
                  <th>Target</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.id}>
                    <td className="mono">{formatAuditTimestamp(a.performed_at)}</td>
                    <td className="mono">#{a.actor_user_id || "—"}</td>
                    <td className="muted">{a.actor_role_snapshot || "—"}</td>
                    <td><Pill tone="blue">{a.action_category}</Pill></td>
                    <td className="mono">{a.action_type}</td>
                    <td className="muted">
                      {a.target_entity_type}{a.target_entity_id ? ` #${a.target_entity_id}` : ""}
                    </td>
                    <td
                      className="muted"
                      title={a.notes || ""}
                      style={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis" }}
                    >
                      {a.notes || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {totalPages > 1 && (
        <div className="toolbar">
          <Btn size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}>Prev</Btn>
          <span className="mono" style={{ fontSize: "var(--t-sm)", color: "var(--tx-3)" }}>
            Page {page} of {totalPages}
          </span>
          <Btn size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</Btn>
        </div>
      )}
    </PageShell>
  );
}

export default AuditLogViewer;
